import {
  IInteractiveModLocation,
  LocationTarget,
  interactiveLocationActions,
  interactiveLocationApps,
  interactiveLocationTargetTypes,
  interactiveLocationTravelModes,
} from "@blings/blings-player";
import { JSONSchema7 } from "json-schema";

export const LOCATION_SOURCE_KEYS = [
  "value",
  "dataKey",
  "expression",
  "liveControlKey",
  "inputName",
] as const;

export type LocationSourceKey = typeof LOCATION_SOURCE_KEYS[number];

export const LOCATION_EXPRESSION_EXAMPLE = `return {
  type: "coordinates",
  latitude: 32.0743,
  longitude: 34.7925,
  label: "Destination",
};

/*
Other supported return formats:

Address:
return {
  type: "address",
  formattedAddress: "1 Apple Park Way, Cupertino, CA 95014",
  label: "Apple Park",
  countryCode: "US",
};

Selected place:
return {
  type: "place",
  label: "Queen Victoria Building",
  latitude: -33.8718,
  longitude: 151.2067,
  formattedAddress: "455 George St, Sydney NSW 2000, Australia",
  providerIds: {
    googlePlaceId: "ChIJISz8NjyuEmsRFTQ9Iw7Ear8",
  },
};

Search query (Show location only):
return {
  type: "query",
  query: "coffee",
  latitude: 32.0743,
  longitude: 34.7925,
};

Plain strings are also supported:
return "1 Apple Park Way, Cupertino, CA 95014";
return "32.0743,34.7925";
*/`;

export const LOCATION_SCALAR_SOURCE_HELP =
  'The selected value is used as an address or place search. Include the city and country to reduce ambiguity. A "latitude,longitude" value is also supported.';

export const LOCATION_TARGET_LABELS: Record<
  interactiveLocationTargetTypes,
  string
> = {
  [interactiveLocationTargetTypes.place]: "Selected place",
  [interactiveLocationTargetTypes.coordinates]: "Coordinates",
  [interactiveLocationTargetTypes.address]: "Address",
  [interactiveLocationTargetTypes.query]: "Search query",
};

const ACTION_LABELS: Record<interactiveLocationActions, string> = {
  [interactiveLocationActions.show]: "Show",
  [interactiveLocationActions.navigate]: "Navigate",
};

const APP_LABELS: Record<interactiveLocationApps, string> = {
  [interactiveLocationApps.system]: "Device default",
  [interactiveLocationApps.ask]: "Ask viewer",
  [interactiveLocationApps.googleMaps]: "Google Maps",
  [interactiveLocationApps.appleMaps]: "Apple Maps",
  [interactiveLocationApps.waze]: "Waze",
};

export const createEmptyLocationTarget = (
  type: interactiveLocationTargetTypes
): LocationTarget => ({ type } as LocationTarget);

export const replaceLocationTargetType = (
  type: interactiveLocationTargetTypes
): LocationTarget => createEmptyLocationTarget(type);

export const updateLocationTargetField = (
  target: LocationTarget,
  key: string,
  value: unknown
): LocationTarget => {
  const next = { ...target } as Record<string, unknown>;
  if (value === "" || value === null || typeof value === "undefined") {
    delete next[key];
  } else {
    next[key] = value;
  }
  return next as LocationTarget;
};

export const updateLocationGooglePlaceId = (
  target: LocationTarget,
  googlePlaceId: string
): LocationTarget => {
  const next = { ...target } as any;
  if (googlePlaceId.trim()) {
    next.providerIds = { googlePlaceId: googlePlaceId.trim() };
  } else {
    delete next.providerIds;
  }
  return next;
};

export const getActiveLocationSource = (
  change: Partial<IInteractiveModLocation>
): LocationSourceKey => {
  return (
    LOCATION_SOURCE_KEYS.find(
      (key) => typeof (change as any)[key] !== "undefined"
    ) || "value"
  );
};

export const getLocationSourceUpdate = (
  source: LocationSourceKey,
  sourceValue: unknown
): { keys: LocationSourceKey[]; values: unknown[] } => ({
  keys: [...LOCATION_SOURCE_KEYS],
  values: LOCATION_SOURCE_KEYS.map((key) =>
    key === source ? sourceValue : undefined
  ),
});

export const getLocationFieldVisibility = (
  action: interactiveLocationActions
) => ({
  zoom: action === interactiveLocationActions.show,
  travelMode: action === interactiveLocationActions.navigate,
});

export const isLocationTravelModeSupported = (
  app: interactiveLocationApps | undefined,
  travelMode: interactiveLocationTravelModes | undefined
): boolean => {
  if (!travelMode) return true;
  if (
    app === interactiveLocationApps.appleMaps &&
    travelMode === interactiveLocationTravelModes.cycling
  ) {
    return false;
  }
  if (
    app === interactiveLocationApps.waze &&
    travelMode !== interactiveLocationTravelModes.driving
  ) {
    return false;
  }
  return true;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const hasText = (value: unknown): value is string =>
  typeof value === "string" && Boolean(value.trim());

export const validateLocationTarget = (
  value: unknown,
  action: interactiveLocationActions | undefined
): string[] => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return ["Complete the required location fields."];
  }

  const target = value as Record<string, any>;
  const errors: string[] = [];
  const hasLatitude = typeof target.latitude !== "undefined";
  const hasLongitude = typeof target.longitude !== "undefined";

  if (
    hasLatitude &&
    (!isFiniteNumber(target.latitude) ||
      target.latitude < -90 ||
      target.latitude > 90)
  ) {
    errors.push("Latitude must be between -90 and 90.");
  }
  if (
    hasLongitude &&
    (!isFiniteNumber(target.longitude) ||
      target.longitude < -180 ||
      target.longitude > 180)
  ) {
    errors.push("Longitude must be between -180 and 180.");
  }

  switch (target.type) {
    case interactiveLocationTargetTypes.place:
      if (!hasText(target.label) || !hasLatitude || !hasLongitude) {
        errors.push("Complete the required location fields.");
      }
      break;
    case interactiveLocationTargetTypes.coordinates:
      if (!hasLatitude || !hasLongitude) {
        errors.push("Complete the required location fields.");
      }
      break;
    case interactiveLocationTargetTypes.address:
      if (!hasText(target.formattedAddress)) {
        errors.push("Complete the required location fields.");
      }
      break;
    case interactiveLocationTargetTypes.query:
      if (!hasText(target.query)) {
        errors.push("Complete the required location fields.");
      }
      if (hasLatitude !== hasLongitude) {
        errors.push(
          "Provide both latitude and longitude, or leave both empty."
        );
      }
      if (action === interactiveLocationActions.navigate) {
        errors.push(
          "Search queries can show results but cannot start navigation."
        );
      }
      break;
    default:
      errors.push("Complete the required location fields.");
  }

  return Array.from(new Set(errors));
};

export const validateDynamicLocationValue = (
  value: unknown,
  action: interactiveLocationActions | undefined
): string[] => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return validateLocationTarget(value, action);
  }
  if (typeof value !== "string") {
    return ["Complete the required location fields."];
  }

  const trimmed = value.trim();
  if (!trimmed) return ["Complete the required location fields."];
  if (!trimmed.startsWith("{")) return [];

  try {
    return validateLocationTarget(JSON.parse(trimmed), action);
  } catch {
    return ["Complete the required location fields."];
  }
};

export const validateLocationMod = (
  change: Partial<IInteractiveModLocation>
): string[] => {
  const errors: string[] = [];
  if (!hasText(change.layerName)) {
    errors.push("Select at least one interactive layer.");
  }
  if (!hasText(change.ctaName)) {
    errors.push("Name this button for analytics.");
  }
  if (!change.action) {
    errors.push("Choose a location action.");
  }
  if (
    !change.app ||
    !Object.values(interactiveLocationApps).includes(change.app)
  ) {
    errors.push("Choose a map application.");
  }

  const source = getActiveLocationSource(change);
  const sourceValue = (change as any)[source];
  const hasSource =
    typeof sourceValue !== "undefined" &&
    (typeof sourceValue !== "string" || Boolean(sourceValue.trim()));
  if (!hasSource) {
    errors.push("Choose a location source.");
  } else if (source === "value") {
    errors.push(...validateLocationTarget(sourceValue, change.action));
  }

  if (typeof change.defaultValue !== "undefined") {
    errors.push(...validateLocationTarget(change.defaultValue, change.action));
  }

  if (
    change.action === interactiveLocationActions.navigate &&
    change.app === interactiveLocationApps.system
  ) {
    errors.push(
      "Device default can show a location but cannot start navigation."
    );
  }
  if (
    change.action === interactiveLocationActions.navigate &&
    change.app === interactiveLocationApps.appleMaps &&
    change.travelMode === interactiveLocationTravelModes.cycling
  ) {
    errors.push("Apple Maps does not support cycling in this connector.");
  }
  if (
    change.action === interactiveLocationActions.navigate &&
    change.app === interactiveLocationApps.waze &&
    change.travelMode &&
    change.travelMode !== interactiveLocationTravelModes.driving
  ) {
    errors.push("Waze supports driving only in this connector.");
  }
  if (
    typeof change.zoom !== "undefined" &&
    (!Number.isInteger(change.zoom) || change.zoom < 2 || change.zoom > 21)
  ) {
    errors.push("Zoom must be an integer between 2 and 21.");
  }

  return Array.from(new Set(errors));
};

export const getLocationSummary = (
  change: Partial<IInteractiveModLocation>
): string => {
  const source = getActiveLocationSource(change);
  let sourceLabel = "";
  if (source === "value" && change.value && typeof change.value === "object") {
    sourceLabel =
      LOCATION_TARGET_LABELS[(change.value as LocationTarget).type] ||
      "Location";
  } else if (source !== "value") {
    sourceLabel = String((change as any)[source] || "Location");
  }

  return [
    change.action ? ACTION_LABELS[change.action] : "Location",
    change.app ? APP_LABELS[change.app] : "",
    sourceLabel,
  ]
    .filter(Boolean)
    .join(" · ");
};

export const LOCATION_DATA_EXAMPLE =
  "1 Apple Park Way, Cupertino, CA 95014, US";

export const createLocationDataSchema = (): JSONSchema7 => ({
  title: "Location address or place",
  description:
    'A complete address or place search that the viewer\'s map app resolves. A "latitude,longitude" string is also supported.',
  type: "string",
  minLength: 1,
  default: LOCATION_DATA_EXAMPLE,
  examples: [LOCATION_DATA_EXAMPLE],
});
